import { type RefObject } from 'react';

import { memoGeneric } from '@/js/libs/memo/index.ts';

import { type SortOrder, SelectionList, type Option, type FormatOptionLabel } from '../../../Components/SelectionList/index.ts';
import Icons from '../../../Elements/Icons/index.ts';
import { type Translations } from '../types.ts';

export interface ListProps<T> {
  mode: 'selection' | 'selected';
  options: Option<T>[];
  total: number;
  singleAction: (option: Option<T>) => void;
  bulkAction: () => void;
  hideBulkActions?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  error?: boolean;
  sortOrder: SortOrder;
  toggleSortOrder: () => void;
  containerHeight: number;
  getOptionKey?: (option: Option<T>) => string;
  formatOptionLabel?: FormatOptionLabel<T>;
  translations: Required<Translations>;
  wrapperRef?: RefObject<HTMLDivElement>;
}

export const List = memoGeneric(function List<T>({
  mode,
  options,
  singleAction,
  bulkAction,
  hideBulkActions,
  total,
  isLoading,
  disabled,
  error,
  sortOrder,
  toggleSortOrder,
  containerHeight,
  getOptionKey,
  translations,
  formatOptionLabel,
  wrapperRef,
}: ListProps<T>) {
  const isSelectionMode = mode === 'selection';

  return (
    <SelectionList<T>
      dataTestId={`${mode}-list`}
      wrapperRef={wrapperRef}
      error={error}
      isLoading={isLoading}
      disabled={disabled}
      translations={{
        header: `${isSelectionMode ? translations.selection : translations.selected}:`,
        footer:
          total === options.length
            ? `${total} ${isSelectionMode ? translations.total : translations.selected}`
            : `${options.length} ${translations.totalSeparator} ${total}`,
        bulkActionTooltip: isSelectionMode ? translations.selectAll : translations.clearAll,
        noEntries: translations.noEntries,
      }}
      onBulkAction={bulkAction}
      bulkActionIcon={hideBulkActions ? undefined : isSelectionMode ? <Icons.Add /> : <Icons.Clear />}
      containerHeight={containerHeight}
      options={options}
      getOptionKey={getOptionKey}
      rowActionIcon={isSelectionMode ? <Icons.Add /> : <Icons.Remove />}
      onRowAction={singleAction}
      formatOptionLabel={formatOptionLabel}
      sortOrder={sortOrder}
      onChangeSortOrder={toggleSortOrder}
    />
  );
});
